import React from 'react'

/* -------------------------------------------------------------
   Product UI mocks for HomeLight Listing Management feature rows.
   Each mock mirrors a real product screenshot.
   ------------------------------------------------------------- */

/* ---------- Inline Lucide-style icon set ---------- */
const ICON_PATHS = {
  'file-text': (
    <React.Fragment>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </React.Fragment>
  ),
  'file-signature': (
    <React.Fragment>
      <path d="M20 19.5V8l-6-6H6a2 2 0 0 0-2 2v15.5" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M3 22h18" />
      <path d="M10 17l4-4 2 2" />
    </React.Fragment>
  ),
  users: (
    <React.Fragment>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </React.Fragment>
  ),
  activity: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  home: (
    <React.Fragment>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2z" />
    </React.Fragment>
  ),
  user: (
    <React.Fragment>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </React.Fragment>
  ),
  star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
  upload: (
    <React.Fragment>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </React.Fragment>
  ),
  mountain: <path d="M8 3l4 8 5-5 5 15H2L8 3z" />,
  'pen-tool': (
    <React.Fragment>
      <path d="M12 19l7-7 3 3-7 7-3-3z" />
      <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
      <path d="M2 2l7.586 7.586" />
      <circle cx="11" cy="11" r="2" />
    </React.Fragment>
  ),
  'chevron-down': <polyline points="6 9 12 15 18 9" />,
  'chevron-right': <polyline points="9 18 15 12 9 6" />,
  bug: (
    <React.Fragment>
      <rect x="8" y="6" width="8" height="14" rx="4" />
      <line x1="12" y1="4" x2="10" y2="2" />
      <line x1="12" y1="4" x2="14" y2="2" />
      <line x1="8" y1="10" x2="4" y2="8" />
      <line x1="8" y1="14" x2="4" y2="14" />
      <line x1="8" y1="18" x2="4" y2="20" />
      <line x1="16" y1="10" x2="20" y2="8" />
      <line x1="16" y1="14" x2="20" y2="14" />
      <line x1="16" y1="18" x2="20" y2="20" />
    </React.Fragment>
  ),
  'clipboard-list': (
    <React.Fragment>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <line x1="12" y1="11" x2="16" y2="11" />
      <line x1="12" y1="16" x2="16" y2="16" />
      <line x1="8" y1="11" x2="8.01" y2="11" />
      <line x1="8" y1="16" x2="8.01" y2="16" />
    </React.Fragment>
  ),
  play: <polygon points="6 4 20 12 6 20 6 4" />,
  check: <polyline points="20 6 9 17 4 12" />,
  'check-circle-2': (
    <React.Fragment>
      <circle cx="12" cy="12" r="10" />
      <polyline points="9 12 11 14 15 10" />
    </React.Fragment>
  ),
  'shield-check': (
    <React.Fragment>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </React.Fragment>
  ),
  'building-2': (
    <React.Fragment>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18" />
      <line x1="2" y1="22" x2="22" y2="22" />
      <line x1="10" y1="6" x2="14" y2="6" />
      <line x1="10" y1="10" x2="14" y2="10" />
      <line x1="10" y1="14" x2="14" y2="14" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </React.Fragment>
  ),
}

function Icon({name, style = {}, strokeWidth}) {
  const path = ICON_PATHS[name]
  if (!path) return null
  const {width = 14, height = 14, color, fill, strokeWidth: styleStroke, ...rest} = style
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill={fill || 'none'}
      stroke={color || 'currentColor'}
      strokeWidth={strokeWidth || styleStroke || 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={rest}
      aria-hidden="true">
      {path}
    </svg>
  )
}

/* ---------- Shared frame + headers ---------- */
function MockFrame({url, children, badge, padding = 22}) {
  return (
    <div
      style={{
        borderRadius: 14,
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 30px 60px -20px rgba(15,39,68,0.20), 0 0 0 1px #e2e8f0',
        position: 'relative',
      }}>
      <div
        style={{
          height: 32,
          background: 'linear-gradient(180deg,#f8fafc,#eef2f7)',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 14px',
        }}>
        <span style={{width: 9, height: 9, borderRadius: '50%', background: '#fb923c'}} />
        <span style={{width: 9, height: 9, borderRadius: '50%', background: '#fbbf24'}} />
        <span style={{width: 9, height: 9, borderRadius: '50%', background: '#22c55e'}} />
        <div
          style={{
            flex: 1,
            height: 18,
            maxWidth: 340,
            margin: '0 auto',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            color: '#94a3b8',
            fontWeight: 600,
            textWrap: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {url}
        </div>
      </div>
      <div style={{padding, background: '#fff'}}>{children}</div>
      {badge && (
        <div
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            zIndex: 2,
            padding: '6px 11px',
            borderRadius: 9999,
            background: '#fff',
            border: '1px solid #e2e8f0',
            fontSize: 10,
            fontWeight: 800,
            color: '#16a34a',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            boxShadow: '0 8px 20px -8px rgba(15,39,68,0.18)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}>
          <span style={{width: 6, height: 6, borderRadius: '50%', background: '#16a34a'}} />
          {badge}
        </div>
      )}
    </div>
  )
}

function ListingTabs({active}) {
  const tabs = [
    {id: 'docs', label: 'DOCS', icon: 'file-text'},
    {id: 'offers', label: 'OFFERS', icon: 'file-signature'},
    {id: 'viewers', label: 'VIEWERS', icon: 'users'},
    {id: 'activity', label: 'ACTIVITY', icon: 'activity'},
  ]
  return (
    <div style={{display: 'flex', gap: 6, padding: '0 0 12px', borderBottom: '1px solid #e2e8f0', marginBottom: 14}}>
      {tabs.map(t => {
        const isActive = active === t.id
        return (
          <div
            key={t.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.08em',
              color: isActive ? '#0c6eba' : '#94a3b8',
              background: isActive ? '#e8f5ff' : 'transparent',
              borderBottom: isActive ? '2px solid #46b6ff' : '2px solid transparent',
            }}>
            <Icon name={t.icon} style={{width: 11, height: 11}} />
            {t.label}
          </div>
        )
      })}
    </div>
  )
}

function PropertyHeader() {
  return (
    <div style={{display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #f1f5f9'}}>
      <div
        style={{
          width: 54,
          height: 42,
          borderRadius: 6,
          background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Icon name="home" style={{width: 18, height: 18, color: '#fff'}} />
      </div>
      <div style={{flex: 1}}>
        <div style={{fontSize: 13, fontWeight: 700, color: '#273653'}}>123 Main Street</div>
        <div style={{fontSize: 10, color: '#94a3b8'}}>San Francisco, CA 94105 · $880,000 · 4 bd / 2.5 ba</div>
      </div>
      <span
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: '#16a34a',
          background: '#dcfce7',
          padding: '3px 8px',
          borderRadius: 9999,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}>
        SHARE PROPERTY
      </span>
    </div>
  )
}

/* 1. Offers dashboard */
export function MockOffers() {
  const offers = [
    {name: 'Gregory Scott', email: 'greg@brokerage.com', phone: '415-555-0066', price: '$1,500,000', finance: 'Loan', star: false, hot: false},
    {name: 'Victoria Sato', email: 'victoria@bizz.com', phone: '415-555-3219', price: '$1,450,000', finance: 'Loan', star: true, hot: false},
    {name: 'Sam Jackson', email: 'sam@brokerage.com', phone: '415-555-1196', price: '$1,447,000', finance: 'Cash', star: false, hot: true},
    {name: 'Gal Costa', email: 'gal@brokerage.com', phone: '415-555-7328', price: '$1,550,000', finance: 'Loan', star: false, hot: false},
  ]
  return (
    <MockFrame url="homelight.com / listing-management / 123-main-street" badge="Sharable with seller">
      <PropertyHeader />
      <ListingTabs active="offers" />
      <div style={{display: 'flex', gap: 8, marginBottom: 12}}>
        <span
          style={{
            padding: '5px 10px',
            borderRadius: 6,
            background: '#46b6ff',
            fontSize: 10,
            fontWeight: 800,
            color: '#fff',
            letterSpacing: '0.06em',
          }}>
          + ADD OFFER
        </span>
        <span
          style={{
            padding: '5px 10px',
            borderRadius: 6,
            background: '#fff',
            border: '1px solid #cbd5e1',
            fontSize: 10,
            fontWeight: 800,
            color: '#475569',
            letterSpacing: '0.06em',
          }}>
          DOWNLOAD SUMMARY
        </span>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8}}>
        {offers.map((o, i) => (
          <div
            key={i}
            style={{
              border: `1px solid ${o.hot ? '#46b6ff' : '#e2e8f0'}`,
              borderRadius: 8,
              padding: 10,
              background: o.hot ? '#f4fbff' : '#fff',
              position: 'relative',
            }}>
            <Icon
              name="star"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 11,
                height: 11,
                color: o.star ? '#fbbf24' : '#cbd5e1',
                fill: o.star ? '#fbbf24' : 'none',
              }}
            />
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 8,
              }}>
              <Icon name="user" style={{width: 14, height: 14, color: '#fff'}} />
            </div>
            <div style={{fontSize: 10.5, fontWeight: 700, color: '#273653'}}>{o.name}</div>
            <div style={{fontSize: 8.5, color: '#94a3b8', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{o.email}</div>
            <div style={{fontSize: 8.5, color: '#94a3b8'}}>{o.phone}</div>
            <div style={{fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 8}}>PRICE</div>
            <div style={{fontSize: 12, fontWeight: 700, color: '#273653'}}>{o.price}</div>
            <div style={{marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4}}>
              <span
                style={{
                  padding: '4px 0',
                  borderRadius: 4,
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  fontSize: 9,
                  fontWeight: 800,
                  color: '#475569',
                  textAlign: 'center',
                  letterSpacing: '0.06em',
                }}>
                VIEW OFFER
              </span>
              <span
                style={{
                  padding: '4px 0',
                  borderRadius: 4,
                  background: '#46b6ff',
                  fontSize: 9,
                  fontWeight: 800,
                  color: '#fff',
                  textAlign: 'center',
                  letterSpacing: '0.06em',
                }}>
                RESPOND
              </span>
            </div>
            <div style={{marginTop: 8, paddingTop: 6, borderTop: '1px solid #f1f5f9'}}>
              <div style={{fontSize: 8.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em'}}>Finance type</div>
              <div style={{fontSize: 10.5, fontWeight: 700, color: o.finance === 'Cash' ? '#15803d' : '#273653'}}>{o.finance}</div>
            </div>
          </div>
        ))}
      </div>
    </MockFrame>
  )
}

/* 2. Seller disclosures & form filling */
export function MockDisclosures() {
  const docs = [
    {name: 'Seller Property Disclosure (TDS)', status: 'Signed', col: '#16a34a', bg: '#dcfce7', date: 'Sep 18'},
    {name: 'Statewide Buyer/Seller Advisory', status: 'Signed', col: '#16a34a', bg: '#dcfce7', date: 'Sep 18'},
    {name: 'Lead-Based Paint Disclosure', status: 'Sent to seller', col: '#0c6eba', bg: '#e8f5ff', date: 'Sep 19'},
    {name: 'Earthquake Hazards Report', status: 'Form-filled', col: '#7c3aed', bg: '#f3e8ff', date: 'Sep 17'},
    {name: 'Coversheet', status: 'Uploaded', col: '#475569', bg: '#f1f5f9', date: 'Sep 16'},
  ]
  const actions = [
    {l: 'Upload Documents', icon: 'upload', on: false},
    {l: 'Order Prelim', icon: 'file-text', on: false},
    {l: 'Order NHD Report', icon: 'mountain', on: false},
    {l: 'Invite Seller to Disclose', icon: 'pen-tool', on: true},
  ]
  return (
    <MockFrame url="homelight.com / listing-management / 123-main-street" badge="It's free">
      <PropertyHeader />
      <ListingTabs active="docs" />
      <div style={{display: 'grid', gridTemplateColumns: '170px 1fr', gap: 14}}>
        <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
          {actions.map((a, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 6,
                background: a.on ? '#46b6ff' : '#fff',
                border: `1px solid ${a.on ? '#46b6ff' : '#e2e8f0'}`,
                color: a.on ? '#fff' : '#273653',
                fontSize: 10.5,
                fontWeight: 700,
              }}>
              <Icon name={a.icon} style={{width: 11, height: 11}} />
              {a.l}
            </div>
          ))}
        </div>
        <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
          {docs.map((d, i) => (
            <div
              key={i}
              style={{
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                display: 'grid',
                gridTemplateColumns: '18px 1fr auto auto',
                gap: 10,
                alignItems: 'center',
              }}>
              <Icon name="file-text" style={{width: 14, height: 14, color: '#64748b'}} />
              <div>
                <div style={{fontSize: 11.5, fontWeight: 700, color: '#273653'}}>{d.name}</div>
                <div style={{fontSize: 9, color: '#94a3b8', marginTop: 1}}>1 page · updated {d.date}</div>
              </div>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: d.col,
                  background: d.bg,
                  padding: '3px 7px',
                  borderRadius: 9999,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}>
                {d.status}
              </span>
              <Icon name="chevron-down" style={{width: 12, height: 12, color: '#cbd5e1'}} />
            </div>
          ))}
        </div>
      </div>
    </MockFrame>
  )
}

/* 3. Document tools — split / annotate / redact / replace */
export function MockDocTools() {
  const toolbar = [{l: 'ADD DOCUMENTS', primary: true}, {l: 'DOWNLOAD'}, {l: 'REVISION'}, {l: 'REORDER'}, {l: 'STAMP'}]
  const docs = [
    {name: 'Coversheet', pages: '1 PAGE', size: '132 KB', date: 'Mar 4 2021 11:24 AM', updated: false},
    {name: 'Earthquake Hazards Report', pages: '7 PAGES', size: '512 KB', date: 'Feb 27 2019 10:00 AM', updated: true},
    {name: 'AVID', pages: '4 PAGES', size: '298 KB', date: 'Feb 18 2019 03:11 PM', updated: false},
  ]
  return (
    <MockFrame url="homelight.com / listing-management / 123-main-street">
      <PropertyHeader />
      <ListingTabs active="docs" />
      <div style={{marginBottom: 12, display: 'flex', gap: 6}}>
        {toolbar.map((b, i) => (
          <span
            key={i}
            style={{
              padding: '5px 10px',
              borderRadius: 6,
              background: b.primary ? '#46b6ff' : '#fff',
              border: b.primary ? '1px solid #46b6ff' : '1px solid #cbd5e1',
              fontSize: 9.5,
              fontWeight: 800,
              color: b.primary ? '#fff' : '#475569',
              letterSpacing: '0.06em',
            }}>
            {b.l}
          </span>
        ))}
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
        {docs.map((d, i) => (
          <div key={i} style={{border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8}}>
              <Icon name="file-text" style={{width: 14, height: 14, color: '#64748b'}} />
              <div style={{flex: 1}}>
                <div style={{fontSize: 11.5, fontWeight: 700, color: '#273653', display: 'flex', alignItems: 'center', gap: 6}}>
                  {d.name}
                  {d.updated && (
                    <span
                      style={{
                        fontSize: 8,
                        fontWeight: 800,
                        color: '#0c6eba',
                        background: '#e8f5ff',
                        padding: '2px 6px',
                        borderRadius: 9999,
                        letterSpacing: '0.06em',
                      }}>
                      UPDATED
                    </span>
                  )}
                </div>
                <div style={{fontSize: 9, color: '#94a3b8', marginTop: 1}}>
                  {d.pages} · {d.size} · UPDATED {d.date}
                </div>
              </div>
            </div>
            <div style={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>
              {['SPLIT', 'ANNOTATE', 'RENAME', 'REPLACE', 'DOWNLOAD', 'DELETE'].map((a, j) => (
                <span
                  key={j}
                  style={{
                    padding: '3px 8px',
                    borderRadius: 4,
                    border: '1px solid #e2e8f0',
                    background: '#fff',
                    fontSize: 8.5,
                    fontWeight: 800,
                    color: '#475569',
                    letterSpacing: '0.06em',
                  }}>
                  {a}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </MockFrame>
  )
}

/* 4. Order Prelim & NHD reports — vendor services */
export function MockReports() {
  const reports = [
    {name: 'Preliminary Title Report', vendor: 'First American', status: 'Ordered', col: '#0c6eba', bg: '#e8f5ff', icon: 'file-text', eta: '2-3 days'},
    {name: 'Natural Hazards Report', vendor: 'JCP-LGS Disclosures', status: 'Delivered', col: '#16a34a', bg: '#dcfce7', icon: 'mountain', eta: 'Ready'},
    {name: 'Termite / Pest Inspection', vendor: 'Available', status: 'Order now', col: '#475569', bg: '#f1f5f9', icon: 'bug', eta: '48 hr'},
    {name: 'Home Inspection', vendor: 'Available', status: 'Order now', col: '#475569', bg: '#f1f5f9', icon: 'clipboard-list', eta: '3 days'},
  ]
  return (
    <MockFrame url="homelight.com / listing-management / 123-main-street / reports">
      <PropertyHeader />
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12}}>
        <div>
          <div style={{fontSize: 10.5, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em'}}>Order reports & services</div>
          <div style={{fontSize: 14, fontWeight: 700, color: '#273653', marginTop: 2}}>One click. No phone tag.</div>
        </div>
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
        {reports.map((r, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '32px 1fr auto',
              gap: 12,
              alignItems: 'center',
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
            }}>
            <span style={{width: 32, height: 32, borderRadius: 8, background: '#f4fbff', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <Icon name={r.icon} style={{width: 14, height: 14, color: '#0c6eba'}} />
            </span>
            <div>
              <div style={{fontSize: 12, fontWeight: 700, color: '#273653'}}>{r.name}</div>
              <div style={{fontSize: 9.5, color: '#94a3b8', marginTop: 1}}>
                {r.vendor} · ETA {r.eta}
              </div>
            </div>
            <span
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: r.col,
                background: r.bg,
                padding: '4px 9px',
                borderRadius: 9999,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>
              {r.status}
            </span>
          </div>
        ))}
      </div>
    </MockFrame>
  )
}

/* 5. Listing Information Package — branded property page */
export function MockListingPackage() {
  const stats = [
    {l: 'PRICE', v: '$880,000'},
    {l: 'BEDS', v: '4'},
    {l: 'BATHS', v: '2.5'},
    {l: 'SQFT', v: '2,200'},
  ]
  const roles = [
    {l: 'I am an agent', sel: true},
    {l: 'I am a buyer', sel: false},
  ]
  return (
    <MockFrame url="homelight.com / listing / 123-main-street" padding={0}>
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <div>
          <div style={{fontSize: 13, fontWeight: 700, color: '#273653'}}>123 Main Street</div>
          <div style={{fontSize: 10, color: '#94a3b8'}}>San Francisco, CA 94105</div>
        </div>
        <span
          style={{
            fontSize: 9,
            fontWeight: 800,
            color: '#0c6eba',
            background: '#e8f5ff',
            padding: '4px 9px',
            borderRadius: 9999,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>
          PRIVATE
        </span>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 0}}>
        <div>
          <div
            style={{
              aspectRatio: '4/3',
              background: 'linear-gradient(135deg,#cbd5e1 0%,#94a3b8 60%,#475569 100%)',
              position: 'relative',
            }}>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: 'radial-gradient(circle at 30% 40%, rgba(255,255,255,0.3), transparent 60%)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                padding: '3px 7px',
                background: 'rgba(0,0,0,0.5)',
                borderRadius: 4,
                fontSize: 9,
                color: '#fff',
                fontWeight: 700,
                backdropFilter: 'blur(4px)',
              }}>
              1 / 24
            </div>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 2, padding: 2}}>
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                style={{
                  aspectRatio: '1',
                  background: `linear-gradient(${135 + i * 40}deg,#cbd5e1,#94a3b8)`,
                  opacity: i === 0 ? 1 : 0.7,
                }}
              />
            ))}
          </div>
          <div style={{padding: '12px 14px'}}>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8}}>
              {stats.map((s, i) => (
                <div key={i}>
                  <div style={{fontSize: 8.5, fontWeight: 800, color: '#94a3b8', letterSpacing: '0.08em'}}>{s.l}</div>
                  <div style={{fontSize: 12, fontWeight: 700, color: '#273653', marginTop: 2}}>{s.v}</div>
                </div>
              ))}
            </div>
            <div style={{marginTop: 10, fontSize: 9, color: '#0c6eba', fontWeight: 700}}>VIRTUAL TOUR LINK</div>
          </div>
        </div>
        <div style={{background: '#f8fafc', padding: 14, borderLeft: '1px solid #f1f5f9'}}>
          <div style={{fontSize: 10, fontWeight: 800, color: '#273653', textTransform: 'uppercase', letterSpacing: '0.08em'}}>
            Request access to the property package
          </div>
          <p style={{margin: '8px 0 12px', fontSize: 9.5, color: '#64748b', lineHeight: 1.4}}>
            Review disclosures &amp; documents that buyers won{'\u2019'}t find on Zillow.
          </p>
          <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
            {roles.map((r, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 10px',
                  border: `1px solid ${r.sel ? '#46b6ff' : '#e2e8f0'}`,
                  borderRadius: 6,
                  background: r.sel ? '#f4fbff' : '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#273653',
                }}>
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: '50%',
                    border: `2px solid ${r.sel ? '#46b6ff' : '#cbd5e1'}`,
                    background: r.sel ? '#46b6ff' : '#fff',
                  }}
                />
                {r.l}
              </div>
            ))}
            <div style={{height: 24, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, marginTop: 4}} />
            <div style={{height: 24, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6}} />
            <div
              style={{
                marginTop: 4,
                padding: 8,
                background: '#22c55e',
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 800,
                color: '#fff',
                textAlign: 'center',
                letterSpacing: '0.08em',
              }}>
              REQUEST INFORMATION
            </div>
          </div>
        </div>
      </div>
    </MockFrame>
  )
}

/* 6. Video tours — Create Video Tour */
export function MockVideoTour() {
  const clips = [
    {room: 'Welcome & exterior', dur: '0:24', done: true},
    {room: 'Living room', dur: '0:18', done: true},
    {room: 'Open-concept kitchen', dur: '0:30', done: true},
    {room: 'Master suite', dur: '0:22', done: false},
    {room: 'Backyard', dur: '', done: false},
  ]
  const tips = ['Clips capped at 30 seconds each', 'Narrate by pointing out the best features', 'Make sure the scene is quiet & well-lit']
  return (
    <MockFrame url="homelight.com / listing-management / 123-main-street / video-tour" badge="No editing required">
      <PropertyHeader />
      <div style={{display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14}}>
        <div>
          <div
            style={{
              aspectRatio: '16/10',
              borderRadius: 8,
              background: 'linear-gradient(135deg,#273653 0%,#1a2540 100%)',
              position: 'relative',
              overflow: 'hidden',
            }}>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: 'radial-gradient(circle at 35% 45%, rgba(70,182,255,0.25), transparent 60%)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 10,
                left: 10,
                padding: '4px 8px',
                background: 'rgba(220,38,38,0.95)',
                borderRadius: 4,
                fontSize: 9,
                color: '#fff',
                fontWeight: 800,
                letterSpacing: '0.08em',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}>
              <span style={{width: 6, height: 6, borderRadius: '50%', background: '#fff'}} /> REC
            </div>
            <div
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                padding: '4px 8px',
                background: 'rgba(0,0,0,0.5)',
                borderRadius: 4,
                fontSize: 9,
                color: '#fff',
                fontWeight: 800,
                letterSpacing: '0.08em',
                backdropFilter: 'blur(4px)',
              }}>
              0:24 / 0:30
            </div>
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%,-50%)',
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.18)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Icon name="play" style={{width: 18, height: 18, color: '#fff', fill: '#fff', marginLeft: 2}} />
            </div>
            <div
              style={{
                position: 'absolute',
                left: 10,
                right: 10,
                bottom: 10,
                height: 3,
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 9999,
              }}>
              <div style={{width: '80%', height: '100%', background: '#46b6ff', borderRadius: 9999}} />
            </div>
          </div>
          <div style={{marginTop: 10, padding: 10, background: '#f4fbff', borderRadius: 6, border: '1px solid #d0ecff'}}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: '#0c6eba',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 6,
              }}>
              Tips for a great clip
            </div>
            {tips.map((t, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  fontSize: 10,
                  color: '#475569',
                  lineHeight: 1.4,
                  marginBottom: i < tips.length - 1 ? 4 : 0,
                }}>
                <Icon name="check-circle-2" style={{width: 11, height: 11, color: '#0c6eba', flexShrink: 0, marginTop: 2}} />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              color: '#273653',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 8,
            }}>
            Tour clips
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
            {clips.map((c, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                }}>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: c.done ? '#22c55e' : '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                  {c.done ? (
                    <Icon name="check" style={{width: 11, height: 11, color: '#fff'}} strokeWidth={3} />
                  ) : (
                    <span style={{fontSize: 9, fontWeight: 800, color: '#94a3b8'}}>{i + 1}</span>
                  )}
                </span>
                <div style={{flex: 1, minWidth: 0}}>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: '#273653',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {c.room}
                  </div>
                </div>
                <span style={{fontSize: 9, color: '#94a3b8', fontVariantNumeric: 'tabular-nums'}}>{c.dur || '\u2014'}</span>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 10,
              padding: 8,
              background: '#46b6ff',
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 800,
              color: '#fff',
              textAlign: 'center',
              letterSpacing: '0.08em',
            }}>
            + ADD NEW CLIP
          </div>
        </div>
      </div>
    </MockFrame>
  )
}

/* 7. Buyer Interest Reports — viewership stats + activity timeline */
export function MockBuyerInterest() {
  const stats = [
    {k: '23', v: 'Interested parties'},
    {k: '187', v: 'Views'},
    {k: '15', v: 'Downloads'},
  ]
  const events = [
    {who: 'Maria Bethania', a: 'updated Coversheet.pdf', t: 'Sep 20, 2018 8:36 AM PST'},
    {who: 'Gal Costa', a: 'downloaded AVID.pdf', t: 'Sep 20, 2018 8:36 AM PST'},
  ]
  return (
    <MockFrame url="homelight.com / listing-management / 123-main-street" badge="Sharable with seller">
      <PropertyHeader />
      <ListingTabs active="activity" />
      <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12}}>
        <span style={{fontSize: 9, fontWeight: 800, color: '#475569', letterSpacing: '0.06em'}}>FILTER:</span>
        <span
          style={{
            padding: '4px 8px',
            borderRadius: 4,
            background: '#fff',
            border: '1px solid #cbd5e1',
            fontSize: 9.5,
            fontWeight: 700,
            color: '#475569',
          }}>
          All (14) {'\u25be'}
        </span>
        <span style={{fontSize: 9, fontWeight: 800, color: '#475569', letterSpacing: '0.06em', marginLeft: 8}}>SORT:</span>
        <span
          style={{
            padding: '4px 8px',
            borderRadius: 4,
            background: '#fff',
            border: '1px solid #cbd5e1',
            fontSize: 9.5,
            fontWeight: 700,
            color: '#475569',
          }}>
          Most recent {'\u25be'}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            padding: '4px 9px',
            borderRadius: 4,
            background: '#46b6ff',
            fontSize: 9,
            fontWeight: 800,
            color: '#fff',
            letterSpacing: '0.06em',
          }}>
          DOWNLOAD VIEWER ACTIVITY
        </span>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 12}}>
        {stats.map((s, i) => (
          <div key={i} style={{padding: '14px 12px', border: '1px solid #e2e8f0', borderRadius: 6, textAlign: 'center'}}>
            <div style={{fontSize: 24, fontWeight: 300, color: '#273653', letterSpacing: '-0.02em'}}>{s.k}</div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginTop: 2,
              }}>
              {s.v}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: 6,
        }}>
        Sep 20, 2018
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
        {events.map((e, i) => (
          <div key={i} style={{display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0'}}>
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
              <Icon name="user" style={{width: 11, height: 11, color: '#fff'}} />
            </div>
            <div style={{flex: 1, fontSize: 10.5, color: '#273653', lineHeight: 1.4}}>
              <strong>{e.who}</strong> {e.a}
              <div style={{fontSize: 9, color: '#94a3b8', marginTop: 1}}>{e.t}</div>
            </div>
          </div>
        ))}
      </div>
    </MockFrame>
  )
}

/* 8. Listing team shared access */
export function MockTeamAccess() {
  const team = [
    {name: 'Marissa Chen', role: 'Listing Agent', access: 'Owner', col: '#0c6eba', bg: '#e8f5ff'},
    {name: 'Sara Kim', role: 'Transaction Coordinator', access: 'Edit', col: '#15803d', bg: '#dcfce7'},
    {name: 'David Mendez', role: 'Assistant', access: 'Edit', col: '#15803d', bg: '#dcfce7'},
    {name: 'Cara Thompson', role: 'Broker', access: 'View', col: '#475569', bg: '#f1f5f9'},
    {name: 'Compass Austin', role: 'Brokerage', access: 'Compliance', col: '#7c3aed', bg: '#f3e8ff'},
  ]
  return (
    <MockFrame url="homelight.com / listing-management / 123-main-street / team">
      <PropertyHeader />
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12}}>
        <div>
          <div style={{fontSize: 10.5, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em'}}>Listing team shared access</div>
          <div style={{fontSize: 14, fontWeight: 700, color: '#273653', marginTop: 2}}>One secure place. Whole team in.</div>
        </div>
        <span
          style={{
            padding: '5px 10px',
            borderRadius: 6,
            background: '#46b6ff',
            fontSize: 10,
            fontWeight: 800,
            color: '#fff',
            letterSpacing: '0.06em',
          }}>
          + INVITE
        </span>
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
        {team.map((m, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '34px 1fr auto',
              gap: 10,
              alignItems: 'center',
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
            }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Icon name={m.role === 'Brokerage' ? 'building-2' : 'user'} style={{width: 14, height: 14, color: '#fff'}} />
            </div>
            <div>
              <div style={{fontSize: 12, fontWeight: 700, color: '#273653'}}>{m.name}</div>
              <div style={{fontSize: 10, color: '#94a3b8', marginTop: 1}}>{m.role}</div>
            </div>
            <span
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: m.col,
                background: m.bg,
                padding: '4px 9px',
                borderRadius: 9999,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>
              {m.access}
            </span>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 10,
          padding: 10,
          background: '#f8fafc',
          borderRadius: 6,
          fontSize: 10.5,
          color: '#64748b',
          lineHeight: 1.5,
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
        }}>
        <Icon name="shield-check" style={{width: 13, height: 13, color: '#16a34a', flexShrink: 0, marginTop: 1}} />
        <span>Trusted by transaction coordinators, assistants, realtors, brokers &amp; brokerages.</span>
      </div>
    </MockFrame>
  )
}

/* 9. Closing Services — modern escrow */
export function MockClosing() {
  const steps = [
    {l: 'Open', d: 'Day 1', done: true},
    {l: 'Inspection', d: 'Day 7', done: true},
    {l: 'Appraisal', d: 'Day 14', done: false, active: true},
    {l: 'Close', d: 'Day 21', done: false},
  ]
  const docs = [
    {name: 'Purchase Agreement', status: 'Executed', col: '#16a34a', bg: '#dcfce7', who: 'All parties'},
    {name: 'Escrow Instructions', status: 'Sent', col: '#0c6eba', bg: '#e8f5ff', who: 'Escrow officer'},
    {name: 'Title Commitment', status: 'In review', col: '#c2410c', bg: '#ffedd5', who: 'Title'},
    {name: 'Loan Estimate', status: 'Pending', col: '#475569', bg: '#f1f5f9', who: 'Lender'},
    {name: 'Wire Instructions', status: 'Verified', col: '#16a34a', bg: '#dcfce7', who: 'Buyer'},
  ]
  return (
    <MockFrame url="homelight.com / listing-management / 123-main-street / closing" badge="Modern escrow">
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12}}>
        <div>
          <div style={{fontSize: 10.5, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em'}}>HomeLight Closing Services</div>
          <div style={{fontSize: 14, fontWeight: 700, color: '#273653', marginTop: 2}}>595 Brixton Ave · close in 21 days</div>
        </div>
        <span
          style={{
            padding: '5px 10px',
            borderRadius: 9999,
            background: '#dcfce7',
            color: '#15803d',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.06em',
          }}>
          ON TRACK
        </span>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, marginBottom: 12}}>
        {steps.map((s, i) => (
          <div
            key={i}
            style={{
              padding: '8px 10px',
              borderRadius: 6,
              background: s.done ? '#dcfce7' : s.active ? '#e8f5ff' : '#f8fafc',
              border: `1px solid ${s.done ? '#86efac' : s.active ? '#46b6ff' : '#e2e8f0'}`,
            }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: s.done ? '#15803d' : s.active ? '#0c6eba' : '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>
              {s.l}
            </div>
            <div style={{fontSize: 11, fontWeight: 700, color: '#273653', marginTop: 2}}>{s.d}</div>
          </div>
        ))}
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
        {docs.map((d, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '18px 1fr auto auto',
              gap: 10,
              alignItems: 'center',
              padding: '9px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
            }}>
            <Icon name="file-text" style={{width: 13, height: 13, color: '#64748b'}} />
            <div>
              <div style={{fontSize: 11.5, fontWeight: 700, color: '#273653'}}>{d.name}</div>
              <div style={{fontSize: 9, color: '#94a3b8', marginTop: 1}}>{d.who}</div>
            </div>
            <span
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: d.col,
                background: d.bg,
                padding: '3px 7px',
                borderRadius: 9999,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>
              {d.status}
            </span>
            <Icon name="chevron-right" style={{width: 12, height: 12, color: '#cbd5e1'}} />
          </div>
        ))}
      </div>
    </MockFrame>
  )
}
